import React, { useEffect, useMemo, useState } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Avatar, Box, Menu, MenuItem, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, FormControlLabel, Switch, Grid, Badge, List, ListItem, ListItemText } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { subscribeNotifications, clearNotifications, dismissNotification } from '../utils/notifications';
import DoneIcon from '@mui/icons-material/Done';
import { useNavigate } from 'react-router-dom';
import { ensureLogin, updateMyPreferences, uploadMyAvatar, getApiHost, fetchProjects, fetchScheduleTasks } from '../utils/api';

const Navbar = ({ onMenuToggle, onLogout, user, onUpdatePreferences, onUserChange }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openProfile, setOpenProfile] = useState(false);
  const [openPrefs, setOpenPrefs] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [prefCompact, setPrefCompact] = useState(Boolean(user?.preferences?.compact));
  const [prefNotifications, setPrefNotifications] = useState(Boolean(user?.preferences?.notifications ?? true));
  const [prefThemeColor, setPrefThemeColor] = useState(user?.preferences?.themeColor || '#1976d2');
  const [prefDarkMode, setPrefDarkMode] = useState(Boolean(user?.preferences?.darkMode));
  const colorOptions = ['#1976d2','#2E7D32','#D32F2F','#6A1B9A','#0097A7','#F57C00','#455A64','#7B1FA2','#C2185B','#388E3C'];

  const initials = useMemo(() => {
    const name = user?.name || '';
    if (!name.trim()) return '?';
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
    return (first + last).toUpperCase();
  }, [user]);

  // Notificações (sininho)
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  useEffect(() => {
    const unsubscribe = subscribeNotifications((items) => setNotifications(items));
    return unsubscribe;
  }, []);
  const handleOpenNotifications = (event) => setNotifAnchorEl(event.currentTarget);
  const handleCloseNotifications = () => setNotifAnchorEl(null);

  // Carrega fotografia do localStorage, se existir
  useEffect(() => {
    if (user?.avatarUrl) {
      const host = getApiHost();
      setPhotoDataUrl(`${host}${user.avatarUrl}`);
    } else if (user?.id) {
      const stored = localStorage.getItem(`user_photo_${user.id}`);
      if (stored) setPhotoDataUrl(stored);
      else setPhotoDataUrl(null);
    } else {
      setPhotoDataUrl(null);
    }
    setPrefCompact(Boolean(user?.preferences?.compact));
    setPrefNotifications(Boolean(user?.preferences?.notifications ?? true));
    setPrefThemeColor(user?.preferences?.themeColor || '#1976d2');
    setPrefDarkMode(Boolean(user?.preferences?.darkMode));
  }, [user]);

  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleOpenProfile = () => {
    setOpenProfile(true);
    handleMenuClose();
  };
  const handleCloseProfile = () => setOpenProfile(false);

  const handleOpenPrefs = () => {
    setOpenPrefs(true);
    handleMenuClose();
  };
  const handleClosePrefs = () => setOpenPrefs(false);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Upload para servidor
    await ensureLogin();
    const updatedUser = await uploadMyAvatar(file);
    onUserChange?.(updatedUser);
  };

  const savePreferences = async (nextPrefs) => {
    await ensureLogin();
    const updated = await updateMyPreferences(nextPrefs);
    onUpdatePreferences?.(updated.preferences || nextPrefs);
  };

  // Verificação periódica de prazos (centralizada)
  useEffect(() => {
    let timerId;
    const notifiedRef = { current: {} };
    const runDeadlineCheck = async () => {
      try {
        if (!prefNotifications) return;
        await ensureLogin();
        const projects = await fetchProjects();
        const now = new Date();
        for (const p of (projects || [])) {
          try {
            const tasks = await fetchScheduleTasks(p.id);
            (tasks || []).forEach((t) => {
              const status = t.status || 'planeado';
              if (status === 'concluido' || status === 'cancelado') return;
              if (t.endDate) {
                const end = new Date(t.endDate);
                const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
                const keySoon = `p${p.id}|t${t.id}|soon`;
                const keyLate = `p${p.id}|t${t.id}|late`;
                if (daysLeft < 0 && !notifiedRef.current[keyLate]) {
                  try {
                    const { pushNotification } = require('../utils/notifications');
                    pushNotification({
                      title: 'Tarefa atrasada',
                      message: `Projeto "${p.title}" — Tarefa "${t.name}" deveria terminar em ${new Date(t.endDate).toLocaleDateString()}.`,
                      severity: 'error',
                      projectId: p.id,
                      taskId: t.id
                    });
                    notifiedRef.current[keyLate] = true;
                  } catch {}
                } else if (daysLeft >= 0 && daysLeft <= 3 && !notifiedRef.current[keySoon]) {
                  try {
                    const { pushNotification } = require('../utils/notifications');
                    pushNotification({
                      title: daysLeft === 0 ? 'Prazo termina hoje' : 'Prazo a expirar',
                      message: `Projeto "${p.title}" — Tarefa "${t.name}" termina em ${new Date(t.endDate).toLocaleDateString()}${daysLeft === 0 ? '' : ` (em ${daysLeft} dia(s))`}.`,
                      severity: daysLeft === 0 ? 'warning' : 'info',
                      projectId: p.id,
                      taskId: t.id
                    });
                    notifiedRef.current[keySoon] = true;
                  } catch {}
                }
              }
              const outs = (() => { try { const arr = JSON.parse(t.outputs || '[]'); return Array.isArray(arr) ? arr : []; } catch(e) { return []; } })();
              outs.forEach((o, idx) => {
                const oStatus = o.status || 'pendente';
                if (oStatus === 'concluido') return;
                const endRef = o.endDate || t.endDate;
                if (!endRef) return;
                const oEnd = new Date(endRef);
                const oDaysLeft = Math.ceil((oEnd - now) / (1000 * 60 * 60 * 24));
                const keyOutSoon = `p${p.id}|t${t.id}|out${idx}|soon`;
                const keyOutLate = `p${p.id}|t${t.id}|out${idx}|late`;
                if (oDaysLeft < 0 && !notifiedRef.current[keyOutLate]) {
                  try {
                    const { pushNotification } = require('../utils/notifications');
                    pushNotification({
                      title: 'Output atrasado',
                      message: `Projeto "${p.title}" — Output "${o.name || '—'}" da tarefa "${t.name}" deveria terminar em ${new Date(endRef).toLocaleDateString()}.`,
                      severity: 'error',
                      projectId: p.id,
                      taskId: t.id
                    });
                    notifiedRef.current[keyOutLate] = true;
                  } catch {}
                } else if (oDaysLeft >= 0 && oDaysLeft <= 3 && !notifiedRef.current[keyOutSoon]) {
                  try {
                    const { pushNotification } = require('../utils/notifications');
                    pushNotification({
                      title: oDaysLeft === 0 ? 'Output termina hoje' : 'Output a expirar',
                      message: `Projeto "${p.title}" — Output "${o.name || '—'}" da tarefa "${t.name}" termina em ${new Date(endRef).toLocaleDateString()}${oDaysLeft === 0 ? '' : ` (em ${oDaysLeft} dia(s))`}.`,
                      severity: oDaysLeft === 0 ? 'warning' : 'info',
                      projectId: p.id,
                      taskId: t.id
                    });
                    notifiedRef.current[keyOutSoon] = true;
                  } catch {}
                }
              });
            });
          } catch {}
        }
      } catch {}
    };
    // Executa ao montar e a cada 60 minutos
    runDeadlineCheck();
    timerId = setInterval(runDeadlineCheck, 60 * 60 * 1000);
    return () => { if (timerId) clearInterval(timerId); };
  }, [prefNotifications]);

  // Logo: usar apenas PNG estático servido pelo frontend

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMenuToggle}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Logo"
            sx={{ height: 36, width: 'auto' }}
          />
        </Box>
        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          IA PRO
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton color="inherit" sx={{ mr: 2 }} onClick={handleOpenNotifications} aria-label="notificações">
            <Badge badgeContent={notifications.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Tooltip title={user?.name || 'Utilizador'}>
            <Avatar 
              sx={{ mr: 2, bgcolor: photoDataUrl ? 'transparent' : 'secondary.main', cursor: 'pointer' }}
              onClick={handleAvatarClick}
              src={photoDataUrl || undefined}
            >
              {!photoDataUrl && initials}
            </Avatar>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleOpenProfile}>Perfil</MenuItem>
            <MenuItem onClick={handleOpenPrefs}>Preferências</MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); onLogout(); }}>Sair</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      {/* Menu de Notificações */}
      <Menu
        anchorEl={notifAnchorEl}
        open={Boolean(notifAnchorEl)}
        onClose={handleCloseNotifications}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360 } }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1">Notificações</Typography>
          <Button size="small" onClick={() => { clearNotifications(); }}>Limpar</Button>
        </Box>
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary">Sem notificações</Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {notifications.slice().reverse().map((n) => (
              <ListItem key={n.id} alignItems="flex-start" sx={{ py: 1, gap: 1 }}>
                <ListItemText 
                  primary={n.title}
                  secondary={`${n.message} • ${new Date(n.timestamp).toLocaleString()}`}
                />
                <IconButton size="small" aria-label="marcar como lida" onClick={() => dismissNotification(n.id)}>
                  <DoneIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}
      </Menu>
      {/* Diálogo de Perfil */}
      <Dialog open={openProfile} onClose={handleCloseProfile} maxWidth="sm" fullWidth>
        <DialogTitle>Perfil do Utilizador</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Avatar src={photoDataUrl || undefined} sx={{ width: 64, height: 64 }}>
              {!photoDataUrl && initials}
            </Avatar>
            <Button variant="outlined" component="label">
              Carregar fotografia
              <input hidden accept="image/*" type="file" onChange={handlePhotoChange} />
            </Button>
          </Stack>
          <TextField
            margin="normal"
            fullWidth
            label="Nome"
            value={user?.name || ''}
            InputProps={{ readOnly: true }}
          />
          <TextField
            margin="normal"
            fullWidth
            label="E-mail"
            value={user?.email || ''}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfile}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Preferências */}
      <Dialog open={openPrefs} onClose={handleClosePrefs} maxWidth="sm" fullWidth>
        <DialogTitle>Preferências</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={<Switch checked={prefNotifications} onChange={(e) => { const v = e.target.checked; setPrefNotifications(v); savePreferences({ compact: prefCompact, notifications: v, themeColor: prefThemeColor }); }} />}
            label="Receber notificações"
          />
          <FormControlLabel
            control={<Switch checked={prefCompact} onChange={(e) => { const v = e.target.checked; setPrefCompact(v); savePreferences({ compact: v, notifications: prefNotifications, themeColor: prefThemeColor }); }} />}
            label="Modo compacto"
          />
          <FormControlLabel
            control={<Switch checked={prefDarkMode} onChange={(e) => { const v = e.target.checked; setPrefDarkMode(v); savePreferences({ compact: prefCompact, notifications: prefNotifications, themeColor: prefThemeColor, darkMode: v }); }} />}
            label="Modo escuro"
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Cor do tema</Typography>
            <Grid container spacing={1}>
              {colorOptions.map((color) => (
                <Grid item key={color}>
                  <Box
                    onClick={() => { setPrefThemeColor(color); savePreferences({ compact: prefCompact, notifications: prefNotifications, themeColor: color, darkMode: prefDarkMode }); }}
                    sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: color, cursor: 'pointer', border: color === prefThemeColor ? '2px solid black' : '2px solid transparent' }}
                    title={color}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrefs}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Navbar;
